import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react';
import { navigation, siteData } from '../data';
import { motion as Motion, AnimatePresence } from 'framer-motion';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isHome = location.pathname === '/';
  const isTransparent = isHome && !scrolled;

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${
        isTransparent 
          ? 'bg-gradient-to-b from-black/80 via-black/40 to-transparent border-transparent' 
          : 'bg-zinc-950 border-white/5 shadow-md'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 md:py-4">
        <div className="flex flex-row justify-between items-center md:flex-col md:items-start md:gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              <span className="text-2xl md:text-3xl font-black uppercase tracking-tighter text-white">
                BANHMI&BOWL
              </span>
            </Link>
          </div>
          
          <div className="hidden md:block">
            <div className="flex items-center space-x-2 lg:space-x-4 -ml-3 xl:ml-0">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-3 py-2 rounded-md text-xs lg:text-sm font-bold uppercase tracking-wider transition-colors ${
                    location.pathname === item.href
                      ? 'text-white'
                      : 'text-white hover:text-white/80'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <a
                href={siteData.company.orderUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-full text-xs lg:text-sm font-bold uppercase tracking-wider transition-all duration-300 shadow-md shadow-red-600/20 hover:-translate-y-0.5"
              >
                Bestil Online
              </a>
            </div>
          </div>

          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-md text-white"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Overlay */}
            <Motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/60 z-[60] backdrop-blur-sm md:hidden"
            />
            
            {/* Sidebar */}
            <Motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 h-full w-[280px] bg-red-600 z-[70] shadow-2xl md:hidden flex flex-col"
            >
              <div className="p-6 flex justify-between items-center border-b border-white/10">
                <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center">
                  <span className="text-2xl font-black uppercase tracking-tighter text-white">
                    BANHMI&BOWL
                  </span>
                </Link>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 -mr-2 text-white hover:bg-white/10 rounded-full transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="flex-1 px-4 py-8 space-y-2 overflow-y-auto">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsOpen(false)}
                    className={`block px-4 py-3 rounded-xl text-lg font-bold uppercase tracking-wider transition-all ${
                      location.pathname === item.href
                        ? 'bg-white text-red-600 shadow-lg'
                        : 'text-white hover:bg-white/10'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
                <a
                  href={siteData.company.orderUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsOpen(false)}
                  className="block px-4 py-3 rounded-xl text-lg font-bold uppercase tracking-wider transition-all bg-zinc-950 text-white text-center shadow-lg mt-4"
                >
                  Bestil Online
                </a>
              </div>

              <div className="p-6 mt-auto border-t border-white/10 bg-white/5">
                <div className="flex flex-col gap-4 text-white/80 text-sm">
                  <div className="flex items-center gap-3">
                    <Phone className="h-4 w-4" />
                    <span>{siteData.company.phone}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4" />
                    <span>{siteData.company.email}</span>
                  </div>
                </div>
              </div>
            </Motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  </>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-zinc-100 text-zinc-900">
      <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div>
            <Link to="/" className="inline-block mb-6">
              <span className="text-3xl font-black uppercase tracking-tighter text-zinc-900">
                BANHMI&BOWL
              </span>
            </Link>
            <p className="text-sm leading-relaxed text-zinc-600">
              Vi leverer madoplevelser i topklasse, uanset om du besøger vores restaurant eller bestiller frokost til din virksomhed.
            </p>
            <a 
              href="https://www.findsmiley.dk/1518893" 
              target="_blank" 
              rel="noopener noreferrer"
              className="mt-4 inline-block text-sm underline text-zinc-600 hover:text-zinc-900 transition-colors"
            >
              Se Fødevarestyrelsens smiley rapporter
            </a>
          </div>
          <div>
            <h3 className="text-zinc-900 text-lg font-bold mb-6 uppercase tracking-wider">Kontakt</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-sm">
                <MapPin className="h-5 w-5 text-zinc-500" />
                <span>{siteData.company.address}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Mail className="h-5 w-5 text-zinc-500" />
                <a href={`mailto:${siteData.company.email}`} className="hover:text-red-600 transition-colors">
                  {siteData.company.email}
                </a>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Phone className="h-5 w-5 text-zinc-500" />
                <a href={`tel:${siteData.company.phone.replace(/\s/g, '')}`} className="hover:text-red-600 transition-colors">
                  {siteData.company.phone}
                </a>
              </div>
              <div className="flex items-center gap-3 text-sm pt-4 border-t border-zinc-200">
                <span className="font-bold">CVR:</span>
                <span>{siteData.company.cvr}</span>
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-zinc-900 text-lg font-bold mb-6 uppercase tracking-wider">Åbningstider</h3>
            <div className="space-y-3 text-sm">
              {siteData.company.openingHours.map((item) => (
                <div key={item.day} className="flex justify-between border-b border-zinc-200 pb-1">
                  <span className="text-zinc-600">{item.day}:</span>
                  <span className="font-medium">{item.hours}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-16 pt-8 border-t border-zinc-200 text-center text-xs text-zinc-500">
          <p>&copy; {new Date().getFullYear()} {siteData.company.name}. Alle rettigheder forbeholdes.</p>
        </div>
      </div>
    </footer>
  );
};
